import {TweenMax, Power2, Power4} from 'gsap';
import {Emitter, EVENTS} from '../../utils/emitter';
import {VideoPlayer} from '../videoPlayer';
import {Window} from '../../utils/controllers/window';
import {clamp} from '../../utils/math/clamp';
import {device} from '../../utils/device';

export class CarouselItem extends Emitter {
  public id: string;
  public index: number;
  public size: any;
  public element: HTMLElement;
  public videoPlayer: VideoPlayer;
  public rolledOver: boolean;
  public rolloverDisabled: boolean;
  public scale3d: number;
  public visualElement: HTMLElement;
  private imageAndRollover: HTMLElement;
  private info: HTMLElement;
  private rolloverVideo: HTMLVideoElement;
  private zoomed: boolean;
  private featureVideoOverlay: HTMLElement;

  constructor(element: HTMLElement) {
    super();

    this.size = {
      width: 0,
      height: 0,
      padding: 0,
      marginTop: 0
    };

    this.id = element.getAttribute('data-storyid');
    this.element = element;

    const videoPlayerElement = this.element.querySelector('.featureVideo video') as HTMLVideoElement;

    if (videoPlayerElement) {
      const videoControls = this.element.querySelector('.videoControls') as HTMLElement;
      const subtitlesElement = this.element.querySelector('.subtitles') as HTMLElement;
      this.videoPlayer = new VideoPlayer(videoPlayerElement, videoControls, subtitlesElement);
      this.videoPlayer.on(EVENTS.play, this.onVideoPlayHandler);
      this.videoPlayer.on(EVENTS.pause, this.onVideoPauseHandler);
    }

    this.rolloverVideo = this.element.querySelector('.rolloverVideo') as HTMLVideoElement;
    this.imageAndRollover = this.element.querySelector('.imageAndRollover');
    this.featureVideoOverlay = this.element.querySelector('.featureVideoOverlay');
    this.info = this.element.querySelector('.info');

    this.visualElement = this.element.querySelector('.visual');
    this.addHandlers();
  }

  private addHandlers() {
    this.visualElement.addEventListener('click', this.onItemSelectHandler);
    this.visualElement.addEventListener('mouseover', this.onItemOverHandler);
    this.visualElement.addEventListener('mouseout', this.onItemOutHandler);
  }

  private onItemSelectHandler = e => {
    if (this.zoomed) return;
    this.emit(EVENTS.select, {item: this, event: e});
  };

  public onItemOverHandler = (e?) => {
    if (this.rolloverVideo && !this.rolloverDisabled) {
      this.showRolloverVideo();
    }
    this.rolledOver = true;
    this.emit(EVENTS.mouseOver, {item: this, event: e});
  };

  public onItemOutHandler = e => {
    if (this.rolloverVideo && !this.zoomed) {
      this.hideRolloverVideo();
    }
    this.emit(EVENTS.mouseOut, {item: this, event: e});
    this.rolledOver = false;
  };

  public showRolloverVideo() {
    TweenMax.to(this.rolloverVideo, 0.25, {autoAlpha: 1});
    this.rolloverVideo.play();
  }

  public hideRolloverVideo() {
    TweenMax.to(this.rolloverVideo, 0.25, {autoAlpha: 0});
    this.rolloverVideo.pause();
  }

  private onVideoPlayHandler = () => {
    if (!device.isTouch()) {
      TweenMax.to(this.info, 0.3, {opacity: 0});
    }
    if (this.videoPlayer && !this.videoPlayer.fullScreen) {
      this.videoPlayer.videoElement.style.display = 'block';
      this.animateOutOverlay();
    }
  };

  private onVideoPauseHandler = () => {
    TweenMax.to(this.info, 0.3, {opacity: 1});
    if (this.videoPlayer && !this.videoPlayer.fullScreen) {
      // TODO: Make this none in animate out
      //this.videoPlayer.videoElement.style.display = 'none';
      this.animateInOverlay();
    }
  };

  public setActive(active) {
    if (active) {
      this.element.classList.add('active');
    } else {
      this.element.classList.remove('active');
    }
  }

  public getActive() {
    return this.element.classList.contains('active');
  }

  public setSize(width, height, padding, marginTop) {
    this.size.width = width;
    this.size.height = height;
    this.size.padding = padding;
    this.size.marginTop = marginTop;
    this.resize();
  }

  public resize = () => {
    TweenMax.set(this.visualElement, {width: this.size.width});

    TweenMax.set(this.element, {
      paddingLeft: this.size.padding,
      paddingRight: this.size.padding
    });

    TweenMax.set(this.imageAndRollover, {marginTop: this.size.marginTop});

    TweenMax.set([this.imageAndRollover], {scale: this.getScale()});

    if (this.videoPlayer) {
      TweenMax.set([this.videoPlayer.videoElement], {scale: this.getScale()});
      TweenMax.set([this.featureVideoOverlay], {scale: this.getScale()});
    }
  };

  public show() {
    this.element.style.visibility = 'visible';
  }

  public hide() {
    this.element.style.visibility = 'hidden';
  }

  public nudgeBack(speed = 0.6) {
    TweenMax.to(this.element, speed, {x: 0, ease: Power4.easeOut});
  }

  public nudgeLeft(speed = 0.6) {
    TweenMax.to(this.element, speed, {x: -40, ease: Power4.easeOut});
  }

  public nudgeRight(speed = 0.6) {
    TweenMax.to(this.element, speed, {x: 40, ease: Power4.easeOut});
  }

  public moveRight(speed) {
    TweenMax.to(this.element, speed, {x: Window.halfWidth, ease: Power2.easeInOut});
  }

  public moveLeft(speed) {
    TweenMax.to(this.element, speed, {x: -Window.halfWidth, ease: Power2.easeInOut});
  }

  public moveBack(speed) {
    TweenMax.to(this.element, speed, {x: 0, ease: Power2.easeInOut});
  }

  private animateInOverlay(speed = 0.2) {
    TweenMax.to(this.featureVideoOverlay, speed, {opacity: 0.5, ease: Power2.easeInOut});
  }

  private animateOutOverlay(speed = 0.2) {
    TweenMax.to(this.featureVideoOverlay, speed, {opacity: 0, ease: Power2.easeInOut});
  }

  public zoom(speed) {
    this.zoomed = true;
    this.element.classList.add('zoomed');
    this.element.style.zIndex = '2';
    // TweenMax.to(this.cursors, 0.5, {autoAlpha : 0});
    this.showRolloverVideo();

    // this.visualElement.style.pointerEvents = `none`;

    const elements = [this.imageAndRollover];
    if (this.videoPlayer) {
      elements.push(this.videoPlayer.videoElement);
      elements.push(this.featureVideoOverlay);
    }

    TweenMax.to(elements, speed, {
      scale: this.getScale(),
      transformOrigin: '50% bottom',
      ease: Power2.easeInOut
    });

    if (this.videoPlayer) {
      this.videoPlayer.showUI(speed);
    }
  }

  public zoomOut(speed) {
    this.zoomed = false;
    this.element.classList.remove('zoomed');
    if (device.isTouch()) {
      this.hideRolloverVideo();
    }

    if (this.videoPlayer) {
      this.videoPlayer.pause();
      this.videoPlayer.hideUI();
    }

    const elements = [this.imageAndRollover];
    if (this.videoPlayer) {
      elements.push(this.videoPlayer.videoElement);
      elements.push(this.featureVideoOverlay);
    }

    const scale = 1;
    TweenMax.to(elements, speed, {
      scale: scale,
      ease: Power2.easeInOut,
      onComplete: () => {
        this.element.style.zIndex = '0';
        if (this.videoPlayer) {
          this.animateOutOverlay();
          this.videoPlayer.videoElement.style.display = `none`;
          // this.visualElement.style.pointerEvents = `all`;
        }
      }
    });
  }

  private getScale() {
    let scale;
    if (this.zoomed) {
      const itemAR = this.size.width / this.size.height;
      const windowAR = Window.width / (this.size.height + this.size.marginTop);
      if (itemAR > windowAR) {
        scale = (this.size.height + this.size.marginTop + 2) / this.size.height;
      } else {
        scale = Window.width / (this.size.width - 2);
      }
    } else {
      scale = 1;
    }
    return scale;
  }

  public update3dEffect(carouselX) {
    const disFromCenterSigned = carouselX + this.element.offsetLeft;
    const disFromCenter = Math.abs(disFromCenterSigned);
    const pct = clamp(0, 1, disFromCenter / Window.halfWidth);
    const pctSigned = clamp(-1, 1, disFromCenterSigned / Window.halfWidth);
    this.scale3d = 0.1 * (1 - pct) + 0.9;
    const titleOpacity = 1 - pct;
    const origin = (2 - (pctSigned + 1)) * 50;

    TweenMax.set(this.visualElement, {scale: this.scale3d, transformOrigin: origin + '% 50%'});
    TweenMax.set(this.info, {opacity: titleOpacity});
  }

  public kill() {
    this.visualElement.removeEventListener('mouseover', this.onItemOverHandler);
    this.visualElement.removeEventListener('mouseout', this.onItemOutHandler);

    if (this.videoPlayer) {
      this.videoPlayer.off(EVENTS.play, this.onVideoPlayHandler);
      this.videoPlayer.off(EVENTS.pause, this.onVideoPauseHandler);
      this.videoPlayer.kill();
    }
  }
}
